import { Either, Option, ReadonlyArray, Runtime, pipe } from "@morphism/fp";
import React from "react";

import { Branch } from "~/Branch";
import * as Context from "~/Context";
import * as Data from "~/Data";
import { Graph, gql } from "~/Graph";
import { ID } from "~/ID";
import { Lifecycle } from "~/Lifecycle";
import { Component as LinkComponent } from "~/Link";
import { Remote } from "~/Remote";
import { Request as GenericRequest } from "~/Request";
import { Time } from "~/Time";

export type WorkOrder = {
  readonly workOrder: ID &
    Partial<Branch & Lifecycle.Submitted> & { readonly description?: string };
};

export namespace WorkOrder {
  export const removeIDPrefix = ({ id }: ID): ID => ({
    id: id.replace(/\D/g, ""),
  });

  export const toString = ({
    workOrder: { id, description },
  }: WorkOrder): string => `WO-${id}${description ? `: ${description}` : ""}`;

  export const use = ({
    id,
    branch,
  }: ID & Partial<Branch>): Remote<Option<WorkOrder>> =>
    pipe(
      WorkOrders.use(branch),
      Remote.map(
        flow(
          ({ workOrders }) => workOrders,
          ReadonlyArray.findFirst(({ workOrder }) => workOrder.id === id)
        )
      )
    );

  export const useOrUndefined = (
    ...args: Parameters<typeof use>
  ): WorkOrder | undefined =>
    pipe(
      use(...args),
      Remote.getOrElseW(() => Option.none),
      Option.toUndefined
    );

  export const useHREF = (workOrder: ID): string =>
    `${Context.useBaseURL()}/#/service/work-orders/${workOrder.id}`;

  export type Fragment = Graph.Generated.WorkOrderFragment;
  export namespace Fragment {
    export const document = () => gql`
      fragment WorkOrder on WorkOrder {
        id
        description
      }
    `;

    export const decode = (encoded: Fragment): WorkOrder => ({
      workOrder: {
        ...ID.from(encoded.id),
        description: encoded.description ?? undefined,
      },
    });
  }

  export type Result = Either.ErrorOr<WorkOrder>;
  export namespace Result {
    export type Fragment = Graph.Generated.WorkOrder__ResultFragment;
    export namespace Fragment {
      export const document = () => gql`
        ${WorkOrder.Fragment.document()}
        ${Graph.Errors.Fragment.document()}

        fragment WorkOrder__Result on WorkOrder__Result {
          ... on WorkOrder {
            ...WorkOrder
          }

          ... on Error {
            ...Error
          }
        }
      `;

      export const decode = (encoded: Fragment): Result =>
        "message" in encoded
          ? Either.left(Error(encoded.message))
          : Either.right(WorkOrder.Fragment.decode(encoded));
    }
  }

  export namespace State {
    export type State = { readonly store: Data.Store.Store<WorkOrder> };
    export const initial = (): State => ({ store: Data.Store.initial() });
  }

  export namespace Response {
    export const definition = () =>
      Runtime.strict({
        id: Runtime.number(),
        date_created: Time.DateTime.Response.runtime(),
        description: Runtime.string(),
      });

    export const decode = (
      encoded: Runtime.ToType<typeof definition>
    ): WorkOrder => ({
      workOrder: {
        id: `${encoded.id}`,
        submitted: encoded.date_created,
        description: encoded.description,
      },
    });
  }

  export const Link = ({
    workOrder,
    noDescription,
    ...props
  }: React.HTML<"a"> & WorkOrder & { readonly noDescription?: boolean }) => (
    <LinkComponent {...props} href={useHREF(workOrder)} target="_blank">
      {noDescription
        ? toString({ workOrder: { id: workOrder.id } })
        : workOrder.description ?? toString({ workOrder })}
    </LinkComponent>
  );
}

export type WorkOrders = {
  readonly workOrders: readonly WorkOrder[];
};

export namespace WorkOrders {
  export const use = (branch?: ID): Remote<WorkOrders> => Request.use(branch);

  export namespace Fragment {
    export const document = () => gql`
      fragment WorkOrders on WorkOrder__HasMany {
        workOrders {
          ... on WorkOrder {
            id
            description
          }
        }
      }
    `;
  }

  export namespace Request {
    /** [API Documentation](http://equipmentshare.pages.internal.equipmentshare.com/equipmentshare-api/develop/#get--work-orders) */
    export const use = (branch?: ID): Remote<WorkOrders> =>
      pipe(
        Remote.Query.use(
          "work-orders",
          flow(
            GenericRequest.fromConfig({
              headers: { "X-Fields": "{id date_created description}" },
              endpoint: `/work-orders${
                branch ? `?branch_id=${branch.id}` : ""
              }`,
            }),

            GenericRequest.Expect.status(200),
            GenericRequest.Expect.json(Response.definition()),
            GenericRequest.onSuccess(Response.decode)
          ),
          {
            staleTime: Time.Duration.minutes(15).duration,
            enabled: branch !== undefined,
            keepPreviousData: true,
          }
        )
      );
  }

  export namespace Response {
    export const definition = () =>
      Runtime.array(WorkOrder.Response.definition());

    export const decode = (
      encoded: Runtime.ToType<typeof definition>
    ): WorkOrders =>
      pipe(
        encoded,
        ReadonlyArray.map(WorkOrder.Response.decode),
        (workOrders) => ({ workOrders })
      );
  }
}
